import _isNil from 'lodash/isNil';
import EventBus from '../lib/EventBus';
import EventTracker from '../EventTracker';
import { ATTACK_OPTION_VALUES, ATTACK_SETTING_NAMES } from '../constants/gameAttackConstants';
import { TRACKABLE_EVENT } from '../constants/trackableEvents';
import { ATTACK_EVENT } from '../constants/eventNames';
import TestController from '../testModule/TestController';
import GameController, { GAME_EVENTS } from './GameController';

/**
 * Set, store and retrieve game attacks.
 *
 * @class GameAttacks
 */
export default class GameAttacks {
    /**
     * @for GameAttacks
     * @constructor
     */
    constructor() {
        /**
         * @property _eventBus
         * @type EventBus
         * @private
         */
        this._eventBus = EventBus;

        /**
         * @property _attacks
         * @type {Object}
         * @default {}
         * @private
         */
        this._attacks = {};

        /**
         * Model properties will be added for each game attack
         * dynamically via `.addGameAttacks()`
         *
         * @property {*}
         * @type {string}
         *
         * this[ATTACK_NAME] = ATTACK_VALUE;
         */

        this.addGameAttacks();
    }

    /**
     * Reset all game attacks to their default values
     * 
     * @for GameAttacks
     * @method reset
     * @chainable
     */
    reset() {
        for (let i = 0; i < ATTACK_OPTION_VALUES.length; i++) {
            const attack = ATTACK_OPTION_VALUES[i];

            this.setAttackOptionByName(attack.name, attack.defaultValue);
        }

        this._eventBus.trigger(ATTACK_EVENT.ATTACK_SETTINGS_UPDATED);

        return this;
    }

    /**
     * Add available game attacks to `_attacks` dictionary
     *
     * @for GameAttacks
     * @method addGameAttacks
     */
    addGameAttacks() {
        for (let i = 0; i < ATTACK_OPTION_VALUES.length; i++) {
            const attack = ATTACK_OPTION_VALUES[i];

            this.addAttack(attack);
        }
    }

    /**
     * @for GameAttacks
     * @method addAttack
     * @param attackProps {object}
     */
    addAttack(attackProps) {
        this._attacks[attackProps.name] = attackProps;
        let attackValue = attackProps.defaultValue;

        if (attackProps.saveToLocalStorage) {
            const attackStorageKey = this.buildStorageName(attackProps.name);
            const storedAttackSetting = JSON.parse(global.localStorage.getItem(attackStorageKey));

            if (!_isNil(storedAttackSetting)) {
                attackValue = storedAttackSetting;
                if (!this.verifyAttackValueNumber(attackProps.name, attackValue)) {
                    attackValue = attackProps.defaultValue;
                }
            }
        }

        this[attackProps.name] = attackValue;
    }

    /**
     * Verify attack value from local storage.
     * If the attack value has a min and max value, it will be checked against those values.
     * If it is outside of the min and max values, it will return false.
     * 
     * @for GameAttacks
     * @method verifyAttackValueNumber
     * @param attackName {string}
     * @param attackValue {string}
     * @returns {boolean}
     */
    verifyAttackValueNumber(attackName, attackValue) {
        const attack = this._attacks[attackName];

        if (attackValue < attack.minValue || attackValue > attack.maxValue) {
            return false;
        }

        return true;
    }

    /**
     * @for GameAttacks
     * @method getDescriptions
     * @return {object}
     */
    getDescriptions() {
        return this._attacks;
    }

    /**
     * Gets the value of a given game attack
     *
     * @for GameAttacks
     * @method getAttackOptionByName
     * @param name {string}
     * @return {object}
     */
    getAttackOptionByName(name) {
        return this[name];
    }

    /**
     * Gets the names of all available game attacks
     * 
     * @returns {Array<string>}
     */
    getAttackOptionsNames() {
        return Object.keys(this._attacks);
    }

    /**
     * Sets a game attack to a given value
     *
     * will fire an event with the `EventBus` is one is registered
     *
     * @for GameAttacks
     * @method setAttackByName
     * @param name {string} name of the attack to change
     * @param value {string} value to set the attack value to
     */
    setAttackOptionByName(name, value) {
        const oldValue = this[name]; //TODO: This can be intresting to track when logging events and adding to the test log.
        const newValue = JSON.parse(value);

        if (this._attacks[name].dataVerification) {
            const functionName = this._attacks[name].dataVerification;
            if(!this[functionName](newValue)) {
                return oldValue;
            }
        }

        this[name] = newValue;

        if (this._attacks[name].saveToLocalStorage) {
            const attackStorageKey = this.buildStorageName(name);
            global.localStorage.setItem(attackStorageKey, JSON.stringify(newValue));
        }

        EventTracker.recordEvent(TRACKABLE_EVENT.SETTINGS, name, newValue);

        if (this._attacks[name].onChangeEventHandler) {
            this._eventBus.trigger(this._attacks[name].onChangeEventHandler, newValue);
        }

        TestController.LOG_Attack_Settings();
        const args = [name, oldValue, newValue];
        GameController.events_recordNew(GAME_EVENTS.ATTACK_SETTING_UPDATE,  ...args);

        this._eventBus.trigger(ATTACK_EVENT.ATTACK_SETTINGS_UPDATED);

        return newValue;
    }

    /**
     * Build a string that can be used as a key for localStorage data
     *
     * @for GameAttacks
     * @method buildStorageName
     * @param attackName {string}
     * @return {string}
     */
    buildStorageName(attackName) {
        return `zlsa.atc.attack.settings.${attackName}`;
    }

    verifyNonMovingMinValue(newValue) {
        const min = newValue;
        const max = this.getAttackOptionByName(ATTACK_SETTING_NAMES.NON_MOVING_MAX_TIME);

        if (min > max) {
            return false;
        }
        return true;
    }

    verifyNonMovingMaxValue(newValue) {
        const min = this.getAttackOptionByName(ATTACK_SETTING_NAMES.NON_MOVING_MIN_TIME);
        const max = newValue;

        if (min > max) {
            return false;
        }
        return true;
    }

    verifyMinSpawnTimeForSybil(newValue) {
        const min = newValue;
        const max = this.getAttackOptionByName(ATTACK_SETTING_NAMES.MAX_SPAWN_TIME_FOR_SYBIL);

        if (min > max) {
            return false;
        }
        return true;
    }

    verifyMaxSpawnTimeForSybil(newValue) {
        const min = this.getAttackOptionByName(ATTACK_SETTING_NAMES.MIN_SPAWN_TIME_FOR_SYBIL);
        const max = newValue;

        if (min > max) {
            return false;
        }
        return true;
    }
}
